/**
 * Data View Component
 *
 * Displays table records in a table format with automatic column generation.
 * Uses the unified query endpoint with signed tokens for DuckDB API access.
 *
 * Features:
 * - Automatic column generation from data structure
 * - Server-side pagination via DuckDB API
 * - Empty state and error handling
 * - Defensive programming for null/undefined data
 */

import React, { useMemo, useCallback } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Table as TableIcon } from 'lucide-react';
import type { TableFieldApiResponse } from '@loader/shared-types';
import type { DataGridRequest, DataGridResponse } from '@loader/data-grid';

import { DataTable, useDataGrid } from '@/components/data-grid';
import { generateColumnsFromData, generateColumnsFromSchema } from '@/lib/tableHelpers';
import { EmptyView } from '@/components/common';
import { useRpc } from '@/lib/rpc';

/**
 * Props for DataView component
 */
interface DataViewProps {
  /** Table ID for server-side data fetching (required) */
  tableId: string;
  /** Version ID to filter records (optional, for server-side filtering by version) */
  versionId?: string;
  /** Visual variant for DataTable: 'card' (default) or 'flush' */
  variant?: 'card' | 'flush';
}

/**
 * DataView Component
 *
 * Renders a server-side data table with automatic column generation,
 * pagination, sorting, and filtering.
 *
 * Features:
 * - Server-side pagination via unified query + DuckDB API
 * - Automatic column generation from data
 * - Optional filtering by version ID
 *
 * @param tableId - Table ID for server-side data fetching
 * @param versionId - Optional version ID to filter records
 */
export const DataView: React.FC<DataViewProps> = ({ tableId, versionId, variant = 'card' }) => {
  const rpc = useRpc();

  /**
   * Custom query function that uses the unified query endpoint.
   * 1. Gets a signed token from /api/private/query
   * 2. Executes the query via DuckDB API
   */
  const customQueryFn = useCallback(async (params: DataGridRequest): Promise<DataGridResponse<Record<string, unknown>>> => {
    // 1. Get signed token from CF Worker
    const tokenRes = await rpc.api.private.query.$post({
      json: {
        table: {
          tableId,
          versionId,
        },
        format: 'json',
        limit: params.limit || 20,
        offset: params.offset || 0,
        sort_by: params.sort_by,
        sort_dir: params.sort_dir,
        filters: params.filters,
      },
    });

    if (!tokenRes.ok) {
      const error = await tokenRes.json().catch(() => ({ error: 'Failed to fetch data' }));
      throw new Error((error as { error?: string }).error || 'Failed to fetch data');
    }

    const { token, url } = await tokenRes.json() as {
      token: string;
      url: string;
    };

    // 2. Execute query via DuckDB API
    const dataRes = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });

    if (!dataRes.ok) {
      throw new Error('Failed to execute query');
    }

    const response = await dataRes.json() as {
      data: Record<string, unknown>[];
      pagination: {
        total: number;
        limit: number;
        offset: number;
      };
      schema?: Array<{ column_id: string; label: string; data_type: string }>;
    };

    // 3. Return in DataGridResponse format
    return {
      data: response.data || [],
      pagination: {
        total: response.pagination?.total || 0,
        limit: response.pagination?.limit || params.limit || 20,
        offset: response.pagination?.offset || params.offset || 0,
        remaining: Math.max(0, (response.pagination?.total || 0) - (response.pagination?.offset || 0) - (response.pagination?.limit || 20)),
      },
      // Pass through schema from DuckDB API (includes all fields, even newly added ones)
      schema: response.schema,
    };
  }, [rpc, tableId, versionId]);

  // Server-side grid management with useDataGrid using custom query function
  const grid = useDataGrid<Record<string, unknown>>({
    customQueryFn,
    queryKeyPrefix: `table-data-${tableId}`,
    extraParams: { versionId }, // Include versionId in query key so data refetches when version changes
    initialPageSize: 20,
    initialSortDir: 'desc',
  });

  // Extract business data from API response
  // API returns full record objects with metadata, we only need the payload
  const rowData = useMemo(() => {
    return grid.data.map((record) => {
      const rec = record as Record<string, unknown>;
      return (rec.payload as Record<string, unknown>) || rec;
    });
  }, [grid.data]);

  // Generate columns from schema (preferred) or fallback to data-based generation
  // Use useMemo to avoid infinite loops - only regenerate when data structure changes
  const columnDefs = useMemo<ColumnDef<Record<string, unknown>>[]>(() => {
    if (grid.data.length === 0) {
      return [];
    }

    // Extract payload from first record for column generation
    const firstRecord = grid.data[0] as Record<string, unknown>;
    const payload =
      (firstRecord.payload as Record<string, unknown>) || firstRecord;

    // Use schema-based column generation if available (for proper date formatting)
    if (grid.schema && grid.schema.length > 0) {
      // Transform DataGridColumnSchema to TableFieldApiResponse format
      // NOTE: DuckDB returns data with label (field name) as keys, not column_id (UUID)
      // So we use label as the column name for matching with data keys
      const schemaFields = grid.schema.map((col) => {
        const colAny = col as unknown as Record<string, unknown>;
        const columnId = (colAny.columnId || colAny.column_id) as string;
        const label = (colAny.label || columnId) as string; // Use label for column name
        const dataType = (colAny.dataType || colAny.data_type) as string;
        return {
          id: columnId,
          name: label, // Use label (field name) since that's what data keys are
          data_type: dataType,
        };
      }) as TableFieldApiResponse[];
      return generateColumnsFromSchema([payload], schemaFields);
    }

    // Fallback to data-based generation (less accurate for dates)
    return generateColumnsFromData([payload]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grid.data.length, grid.schema]);

  // Show error state
  if (grid.error) {
    return (
      <EmptyView
        title="Error loading data"
        description={grid.error.message}
        icon={TableIcon}
        className="h-64"
      />
    );
  }

  // Render server-side data table
  return (
    <div className="h-full w-full">
      <DataTable
        data={rowData}
        columns={columnDefs}
        // Pagination state
        pageIndex={grid.pagination.pageIndex}
        pageSize={grid.pagination.pageSize}
        totalItems={grid.pagination.total}
        pageCount={grid.pagination.pageCount}
        onPageChange={grid.actions.setPage}
        onPageSizeChange={grid.actions.setPageSize}
        // Sorting state
        sortBy={grid.sorting.sort_by}
        sortDir={grid.sorting.sort_dir}
        onSortChange={grid.actions.setSort}
        // Filtering state
        filters={grid.filters}
        onFilterChange={grid.actions.setFilter}
        onFilterRemove={grid.actions.removeFilter}
        onFiltersClear={grid.actions.clearFilters}
        // Loading states
        isLoading={grid.isLoading}
        isFetching={grid.isFetching}
        // Enable all server-side features
        enableFiltering={true}
        enablePagination={true}
        showFooter={true}
        // Visual variant
        variant={variant}
        // Empty state
        emptyStateComponent={
          <EmptyView
            title="No data"
            description="No records found to display."
            icon={TableIcon}
          />
        }
      />
    </div>
  );
};
